/**
 * MFE-Workspace Route Configuration
 *
 * Exports route objects (not JSX) for the workspace MFE.
 * Guards and redirects are handled in loaders and the App component.
 *
 * Used by Shell to compose the global router via createBrowserRouter.
 */

import React from 'react'
import type { RouteObject, LoaderFunctionArgs } from 'react-router-dom'
import { redirect } from 'react-router-dom'
import { AuthStateManager, PreferencesManager } from '@innolab-trader/mfe-shared'
import { StorageEngine } from '@innolab-trader/storage'

// Lazy-load the Workspace MFE App component
const WorkspaceApp = React.lazy(() => import('./App'))

/**
 * Workspace route guard loader
 * Ensures user is authenticated AND has selected trading environment
 * Otherwise redirects to appropriate auth page
 */
async function workspaceLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const next = encodeURIComponent(url.pathname + (url.search || ''))
  const auth = await AuthStateManager.load()
  const isAuthenticated = auth ? AuthStateManager.isAuthValid(auth) : false

  if (!isAuthenticated) {
    return redirect(`/welcome?next=${next}`)
  }

  const sessionPrefs = await PreferencesManager.loadSessionPrefs(StorageEngine.session())
  const hasTradingEnv = !!sessionPrefs.tradingEnvironment

  if (!hasTradingEnv) {
    return redirect(`/trading-mode?next=${next}`)
  }

  return null
}

export function getWorkspaceRoutes(): RouteObject[] {
  return [
    {
      path: '/workspace',
      element: <WorkspaceApp />,
      loader: workspaceLoader,
    },
    {
      path: '/',
      loader: () => redirect('/workspace'),
    },
  ]
}
